import { Location, ViewportScroller } from '@angular/common';
import { Component, Input, OnInit, WritableSignal, inject, signal } from '@angular/core';
import { DocumentReference } from '@angular/fire/firestore';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { NavigationComponent } from '../../components/navigation/navigation.component';
import { AssessmentsService } from '../../services/assessments.service';
import { AratData, TwoHandsNumberDataSet, initDataARAT } from '../../services/model';
import { getDateIsoString, getDateIsoStringPlus1Minute } from '../../services/util.functions';
import { AssessmentBaseComponent } from '../assessment-base/assessment-base.component';
import { DynamicTableInputComponent } from '../dynamic-table-input/dynamic-table-input.component';

@Component({
  selector: 'app-arat',
  standalone: true,
  imports: [
    DynamicTableInputComponent,
    MatCardModule,
    FormsModule,
    MatFormFieldModule,
    MatButtonModule,
    MatInputModule,
    ReactiveFormsModule,
    MatIconModule,
    MatTooltipModule,
    RouterModule,
    MatExpansionModule,
    NavigationComponent,
  ],
  templateUrl: './arat.component.html',
  styleUrl: './arat.component.scss',
})
export class AratComponent extends AssessmentBaseComponent implements OnInit {
  public location: Location = inject(Location);
  @Input() assessmentId: string | undefined;
  protected readonly anleitung =
    'Der Score geht von 3 (gut) bis 0 (schlecht). Dabei ist der Score teilweise an die Ausführungszeit gekoppelt, benötigt aber letztlich eine qualitative Einschätzung des Therapeuten:\n\n' +
    '3: Angemessen ausgeführt und in unter 5 s. (“Angemessen” hängt von der Aufgabe ab und ist in der deutschen Erklärung genauer festgelegt.)\n\n' +
    '2: Komplett ausgeführt in 5 s bis 60 s.\n\n' +
    '1: Nach 60 s nur teilweise ausgeführt.\n\n' +
    '0: Nach 60 s nicht ausgeführt.\n\n';
  private assessmentService: AssessmentsService = inject(AssessmentsService);

  private scroller: ViewportScroller = inject(ViewportScroller);
  private fb: FormBuilder = inject(FormBuilder);
  assessmentData: WritableSignal<AratData | undefined> = signal<AratData | undefined>(undefined);
  assessmentDataNew: AratData = JSON.parse(JSON.stringify(initDataARAT));
  hasError = signal({
    grasp: false,
    grip: false,
    pinch: false,
    gross: false,
  });
  hasTriedToSave = signal(false);
  hasMissingInputs = signal({
    grasp: true,
    grip: true,
    pinch: true,
    gross: true,
  });

  form: FormGroup = this.fb.group({
    assessmentTime: [getDateIsoStringPlus1Minute(), Validators.required],
    note: [''],
  });

  onGraspData(data: {
    data: Array<TwoHandsNumberDataSet>;
    note: string;
    hasError: boolean;
    hasMissingInputs: boolean;
  }) {
    // console.log('onGraspData', data);
    // this.assessmentDataNew.grasp.score = null;
    this.assessmentDataNew.grasp.data = data.data;
    this.assessmentDataNew.grasp.note = data.note;
    this.hasError.update(current => ({
      ...current,
      grasp: data.hasError,
    }));
    this.hasMissingInputs.update(current => ({
      ...current,
      grasp: data.hasMissingInputs,
    }));
  }
  onGripData(data: { data: Array<TwoHandsNumberDataSet>; note: string; hasError: boolean; hasMissingInputs: boolean }) {
    // console.log('onGripData', data);
    // this.assessmentDataNew.grip.score = null;
    this.assessmentDataNew.grip.data = data.data;
    this.assessmentDataNew.grip.note = data.note;
    this.hasError.update(current => ({
      ...current,
      grip: data.hasError,
    }));
    this.hasMissingInputs.update(current => ({
      ...current,
      grip: data.hasMissingInputs,
    }));
  }
  onPinchData(data: {
    data: Array<TwoHandsNumberDataSet>;
    note: string;
    hasError: boolean;
    hasMissingInputs: boolean;
  }) {
    // console.log('onPinchData', data);
    // this.assessmentDataNew.pinch.score = null;
    this.assessmentDataNew.pinch.data = data.data;
    this.assessmentDataNew.pinch.note = data.note;
    this.hasError.update(current => ({
      ...current,
      pinch: data.hasError,
    }));
    this.hasMissingInputs.update(current => ({
      ...current,
      pinch: data.hasMissingInputs,
    }));
  }
  onGrossData(data: {
    data: Array<TwoHandsNumberDataSet>;
    note: string;
    hasError: boolean;
    hasMissingInputs: boolean;
  }) {
    // console.log('onGrossData', data);
    // this.assessmentDataNew.gross.score = null;
    this.assessmentDataNew.gross.data = data.data;
    this.assessmentDataNew.gross.note = data.note;
    this.hasError.update(current => ({
      ...current,
      gross: data.hasError,
    }));
    this.hasMissingInputs.update(current => ({
      ...current,
      gross: data.hasMissingInputs,
    }));
  }

  ngOnInit(): void {
    this.scroller.scrollToPosition([0, 0]);

    if (this.assessmentId) {
      this.assessmentService.getAssessment(this.assessmentId).then(data => {
        const aratData: AratData = data.data as AratData;
        // add manual
        aratData.grasp.manual = initDataARAT.grasp.manual;
        aratData.grip.manual = initDataARAT.grip.manual;
        aratData.gross.manual = initDataARAT.gross.manual;
        aratData.pinch.manual = initDataARAT.pinch.manual;
        this.assessmentData.set(aratData);
        const date = getDateIsoString(new Date(data.dateIso));
        this.form.controls['assessmentTime'].patchValue(date);

        let note = data.note;
        const hasNewNote = note && note.length > 0;
        if (!hasNewNote) {
          // try migration
          note = '';
          note += aratData.grasp.note ? '1. Greifen: ' + aratData.grasp.note + '\n' : '';
          note += aratData.grip.note ? '2. Griff: ' + aratData.grip.note + '\n' : '';
          note += aratData.pinch.note ? '3. Präzisionsgriff: ' + aratData.pinch.note + '\n' : '';
          note += aratData.gross.note ? '4. Basale Armbewegung: ' + aratData.gross.note + '\n' : '';
          aratData.grasp.note = '';
          aratData.grip.note = '';
          aratData.gross.note = '';
          aratData.pinch.note = '';
        }
        this.form.controls['note'].patchValue(note);

        this.assessmentDataNew = JSON.parse(JSON.stringify(this.assessmentData()));
      });
    } else {
      this.assessmentData.set(initDataARAT);
      this.assessmentDataNew = JSON.parse(JSON.stringify(this.assessmentData()));
    }
  }
  onSave() {
    console.log('chris hasMissingInputs', this.hasMissingInputs());

    this.hasTriedToSave.set(true);
    if (this.hasError().grasp || this.hasError().grip || this.hasError().gross || this.hasError().pinch) {
      return;
    }
    if (
      this.hasMissingInputs().grasp ||
      this.hasMissingInputs().grip ||
      this.hasMissingInputs().gross ||
      this.hasMissingInputs().pinch
    ) {
      return;
    }

    if (this.assessmentId) {
      this.assessmentService
        .updateAssessment(this.assessmentId, {
          type: 'ARAT',
          dateIso: this.form.controls['assessmentTime'].value,
          note: this.form.controls['note'].value ?? '',
          data: this.assessmentDataNew,
        })
        .then(res => {
          this.router.navigate(['../../../reports/assessments'], {
            relativeTo: this.route,
            queryParams: { type: 'ARAT', dId: this.assessmentId },
          });
        });
    } else {
      this.assessmentService
        .createAssessment({
          type: 'ARAT',
          dateIso: this.form.controls['assessmentTime'].value,
          note: this.form.controls['note'].value ?? '',
          data: this.assessmentDataNew,
        })
        .then((docRef: DocumentReference) => {
          this.router.navigate(['../../reports/assessments'], {
            relativeTo: this.route,
            queryParams: { type: 'ARAT', dId: docRef.id },
          });
        });
    }
  }

  onNavigateBack() {
    if (this.assessmentId) {
      // this.router.navigate(['../../../reports/assessments'], { relativeTo: this.route });
      this.location.back();
    } else {
      // this.router.navigate(['../../reports/assessments'], { relativeTo: this.route });
      this.router.navigate(['../../assessments/new'], { relativeTo: this.route });
    }
  }
}
